import React from 'react';
import {
    AppBar,
    Toolbar,
    Typography,
    Button,
    Box,
    IconButton,
    Chip
} from '@mui/material';
import {
    Home,
    CalendarToday,
    Schedule,
    Message,
    Settings,
    Wifi,
    WifiOff
} from '@mui/icons-material';

const TopMenuBar = ({ activePage, onPageChange, connectionStatus, onConnectionToggle }) => {
    const menuItems = [
        {
            id: 'home',
            label: 'Home',
            icon: <Home fontSize="small" />,
            color: '#2196f3'
        },
        {
            id: 'manual-booking',
            label: 'Manual Booking',
            icon: <CalendarToday fontSize="small" />,
            color: '#2196f3'
        },
        {
            id: 'staff-availability',
            label: 'Staff Availability',
            icon: <Schedule fontSize="small" />,
            color: '#9c27b0'
        },
        {
            id: 'communications',
            label: 'Communications',
            icon: <Message fontSize="small" />,
            color: '#ff5722'
        },
        {
            id: 'settings',
            label: 'Settings',
            icon: <Settings fontSize="small" />,
            color: '#ff9800'
        }
    ];

    const handleConnectionToggle = () => {
        onConnectionToggle();
    };

    return (
        <AppBar
            position="fixed"
            sx={{
                zIndex: 1201,
                background: `linear-gradient(160deg, #FFC107 0%, #FFD54F 50%, #FFC107 100%)`,
                boxShadow: '0px 6px 20px rgba(0, 0, 0, 0.25)',
                borderBottom: '1px solid rgba(255, 255, 255, 0.5)',
                padding: '4px 0'
            }}
        >
            <Toolbar
                sx={{
                    justifyContent: 'space-between',
                    px: 3,
                    minHeight: '80px',
                    position: 'relative',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '40%',
                        background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0.4), rgba(255, 255, 255, 0))',
                        borderRadius: '4px 4px 0 0',
                        pointerEvents: 'none'
                    }
                }}
            >
                {/* Left side - Logo/Brand */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <img
                        src="/logo.png"
                        alt="Eclipse Dashboard"
                        style={{
                            height: '64px',
                            width: 'auto',
                            objectFit: 'contain'
                        }}
                    />
                    <Typography
                        variant="h5"
                        component="div"
                        sx={{
                            fontWeight: 'bold',
                            color: '#333333',
                            letterSpacing: 1,
                            textShadow: '0px 1px 2px rgba(255, 255, 255, 0.5)'
                        }}
                    >
                        Management Dashboard
                    </Typography>
                </Box>

                {/* Center - Navigation Menu */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                    {menuItems.map((item) => (
                        <Button
                            key={item.id}
                            startIcon={item.icon}
                            onClick={() => onPageChange(item.id)}
                            sx={{
                                color: activePage === item.id ? '#FFFFFF' : '#333333',
                                bgcolor: activePage === item.id ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.2)',
                                borderRadius: 2,
                                px: 3,
                                py: 1.5,
                                textTransform: 'none',
                                fontSize: '1rem',
                                fontWeight: activePage === item.id ? 600 : 500,
                                minWidth: 'auto',
                                boxShadow: activePage === item.id ? 'inset 0px 2px 5px rgba(0, 0, 0, 0.2)' : '0px 2px 5px rgba(0, 0, 0, 0.1)',
                                '&:hover': {
                                    bgcolor: activePage === item.id ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.3)',
                                    color: activePage === item.id ? '#FFFFFF' : '#000000'
                                },
                                transition: 'all 0.2s ease-in-out'
                            }}
                        >
                            {item.label}
                        </Button>
                    ))}
                </Box>

                {/* Right side - Connection Status */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip
                        icon={connectionStatus === 'connected' ? <Wifi fontSize="small" /> : <WifiOff fontSize="small" />}
                        label={connectionStatus === 'connected' ? 'Connected' :
                            connectionStatus === 'connecting' ? 'Connecting...' : 'Disconnected'}
                        size="medium"
                        variant="filled"
                        sx={{
                            color: connectionStatus === 'connected' ? '#FFFFFF' :
                                connectionStatus === 'connecting' ? '#FFFFFF' : '#FFFFFF',
                            bgcolor: connectionStatus === 'connected' ? 'rgba(76, 175, 80, 0.9)' :
                                connectionStatus === 'connecting' ? 'rgba(255, 152, 0, 0.9)' : 'rgba(244, 67, 54, 0.9)',
                            borderColor: connectionStatus === 'connected' ? '#4caf50' :
                                connectionStatus === 'connecting' ? '#ff9800' : '#f44336',
                            padding: '4px 8px',
                            height: '36px',
                            boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.2)',
                            fontWeight: 'bold',
                            '& .MuiChip-icon': {
                                color: 'inherit'
                            }
                        }}
                    />
                    <IconButton
                        onClick={handleConnectionToggle}
                        size="medium"
                        sx={{
                            color: connectionStatus === 'connected' ? '#4caf50' : '#f44336',
                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                            width: '40px',
                            height: '40px',
                            '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 0.4)'
                            },
                            boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.1)'
                        }}
                        disabled={connectionStatus === 'connecting'}
                    >
                        {connectionStatus === 'connected' ? <Wifi /> : <WifiOff />}
                    </IconButton>
                </Box>
            </Toolbar>
        </AppBar>
    );
};

export default TopMenuBar;
